// Pure time helpers for the manual deliveries page. Rows carry HH:MM times in their own zone, so those are formatted
// directly. Anything that turns an instant into wall-clock time (the shop's now, a quote's pickup) takes the shop's
// IANA zone explicitly; nothing here reads the browser's zone. 12-hour clock everywhere.

const pad = (n) => String(n).padStart(2, "0");
const DAY_MS = 24 * 60 * 60 * 1000;

export const FIRST_START = "08:00";
export const LAST_START = "20:00";
export const WINDOW_MINUTES = 120;

export function toMinutes(hhmm) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm || "");
  if (!match) return NaN;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function fromMinutes(total) {
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

export function addMinutes(hhmm, minutes) {
  const total = toMinutes(hhmm);
  return Number.isNaN(total) ? "" : fromMinutes(total + minutes);
}

// "13:30" -> "1:30 PM"
export function formatClock(hhmm) {
  const total = toMinutes(hhmm);
  if (Number.isNaN(total)) return "";
  const hours = Math.floor(total / 60) % 24;
  return `${hours % 12 || 12}:${pad(total % 60)} ${hours >= 12 ? "PM" : "AM"}`;
}

function joinRange(start, end) {
  if (!start || !end) return "";
  return start.slice(-2) === end.slice(-2)
    ? `${start.slice(0, -3)} – ${end}`
    : `${start} – ${end}`;
}

// The panel's read-only window: "1:00 – 3:00 PM", "11:00 AM – 1:00 PM".
export function windowText(startHhmm, minutes = WINDOW_MINUTES) {
  return joinRange(
    formatClock(startHhmm),
    formatClock(addMinutes(startHhmm, minutes))
  );
}

export function startTimeOptions() {
  const options = [];
  for (let m = toMinutes(FIRST_START); m <= toMinutes(LAST_START); m += 30) {
    const value = fromMinutes(m);
    options.push({ value, label: formatClock(value) });
  }
  return options;
}

// On the shop's today, start times at or before the shop's current time cannot be picked; on a date before it
// (a form left open past midnight), none can.
export function startOptionsFor(dateIso, now) {
  return startTimeOptions().map((option) => ({
    ...option,
    disabled:
      !!now &&
      (dateIso < now.date ||
        (dateIso === now.date &&
          toMinutes(option.value) <= toMinutes(now.hhmm))),
  }));
}

// { date: "YYYY-MM-DD", hhmm: "HH:MM" } of an instant in timeZone, or null.
export function zonedParts(instant, timeZone) {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (!instant || Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type) => (parts.find((p) => p.type === type) || {}).value;
  const hour = get("hour") === "24" ? "00" : get("hour");
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    hhmm: `${hour}:${get("minute")}`,
  };
}

export function shopNow(timeZone, now = new Date()) {
  return zonedParts(now, timeZone);
}

function isoToUtc(isoDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || "");
  if (!match) return null;
  return new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );
}

export function addDays(isoDate, days) {
  const date = isoToUtc(isoDate);
  if (!date) return "";
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function daysBetween(fromIso, toIso) {
  const a = isoToUtc(fromIso);
  const b = isoToUtc(toIso);
  return a && b ? Math.round((b - a) / DAY_MS) : NaN;
}

function formatIsoDate(isoDate, options) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    ...options,
  }).format(isoToUtc(isoDate));
}

export function shortDate(isoDate) {
  return isoToUtc(isoDate)
    ? formatIsoDate(isoDate, { month: "short", day: "numeric" })
    : "";
}

export function dayLabel(isoDate, todayIso) {
  if (!isoToUtc(isoDate)) return "";
  const md = shortDate(isoDate);
  const diff = daysBetween(todayIso, isoDate);
  if (diff === 0) return `Today, ${md}`;
  if (diff === 1) return `Tomorrow, ${md}`;
  if (diff === -1) return `Yesterday, ${md}`;
  return `${formatIsoDate(isoDate, { weekday: "short" })}, ${md}`;
}

export function nextDays(todayIso, count = 7) {
  return Array.from({ length: count }, (_, i) => {
    const iso = addDays(todayIso, i);
    let top = formatIsoDate(iso, { weekday: "short" });
    if (i === 0) top = "Today";
    if (i === 1) top = "Tmrw";
    return { iso, top, num: Number(iso.slice(8, 10)), long: dayLabel(iso, todayIso) };
  });
}

export function defaultRange(todayIso) {
  return { from: todayIso, to: addDays(todayIso, 30) };
}

export function rangeLabel(fromIso, toIso) {
  return `${shortDate(fromIso)} – ${shortDate(toIso)}`;
}

function compactPart(hhmm) {
  const total = toMinutes(hhmm);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return {
    text: `${hours % 12 || 12}${minutes ? `:${pad(minutes)}` : ""}`,
    half: hours >= 12 ? "PM" : "AM",
  };
}

// The list's window column from the row's own HH:MM strings: "1–3 PM", "11 AM–1 PM", "9:30–11:30 AM".
export function compactWindow(startHhmm, endHhmm) {
  if (Number.isNaN(toMinutes(startHhmm)) || Number.isNaN(toMinutes(endHhmm))) {
    return "";
  }
  const a = compactPart(startHhmm);
  const b = compactPart(endHhmm);
  return a.half === b.half
    ? `${a.text}–${b.text} ${b.half}`
    : `${a.text} ${a.half}–${b.text} ${b.half}`;
}

// The pickup a courier quote will request (quote details, see QuoteAll.details_for). Only Trexity `direct` has a
// known end; a fixed city window shows its start. "" when the quote has no pickup time.
export function pickupRange(details) {
  if (!details || !details.pickup_start_at) return "";
  const zone = details.time_zone;
  const start = zonedParts(details.pickup_start_at, zone);
  if (!start) return "";
  const end = details.pickup_end_at
    ? zonedParts(details.pickup_end_at, zone)
    : null;
  if (!end) return `from ${formatClock(start.hhmm)}`;
  const range = joinRange(formatClock(start.hhmm), formatClock(end.hhmm));
  return end.date === start.date ? range : `${range} (next day)`;
}

export function zoneAbbreviation(instant, timeZone) {
  const date = new Date(instant);
  if (!instant || Number.isNaN(date.getTime())) return "";
  const part = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "short",
  })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName");
  return part ? part.value : "";
}

export function countdownText(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}
